import { Note, DianNote, NoteValue, DianNoteValue } from '@shared-models';

import { ArticleMapper } from './article.mapper';
import { InvoiceMapper } from './invoice.mapper';

export class NoteMapper {
  static mapper(note: Note, withoutReference = false): DianNote {
    return {
      id_nota: note.id_nota.toString(),
      tipo_nota: this.mapNoteType(note),
      concepto: note.concepto,
      total: note.total.toFixed(2),
      dueDate: note.dueDate,
      valor_notas: this.mapNoteValues(note.valor_notas, note),
      billyNotes: {
        note_number: note.billyNotes[0].note_number,
      },
      // The referenced invoice is sent without its lines, as before: the note's
      // own lines are valor_notas. The sale lines are fetched only to resolve
      // each note line's unit (see resolveSaleUnitId), and mapping them here
      // would both change the payload and break on the partial selection.
      invoice: InvoiceMapper.mapper({ ...note.invoice, venta: [] }),
      // The invoice keeps travelling in the payload; this only tells api-dian to
      // leave the BillingReference element out of the XML.
      without_reference: withoutReference,
    };
  }

  private static mapNoteType(note: Note): 'Credit' | 'Debit' {
    return note.tipo_nota === 'Credito' ? 'Credit' : 'Debit';
  }

  private static mapNoteValues(
    noteValues: NoteValue[],
    note: Note,
  ): DianNoteValue[] {
    return (
      noteValues.map(
        (value): DianNoteValue => ({
          cantidad: Math.abs(value.cantidad).toFixed(2),
          precio: Math.abs(value.precio).toFixed(2),
          descuento: Math.abs(value.descuento).toFixed(2),
          subtotal: Math.abs(value.subtotal).toFixed(2),
          articulo: ArticleMapper.mapper(
            value.articulo[0],
            this.resolveSaleUnitId(value, note),
          ),
        }),
      ) ?? []
    );
  }

  /**
   * A note line must be reported in the same unit as the invoice line it
   * adjusts, since its quantity comes from that line. `valor_notas` stores no
   * link to the sale line, so the match is by article code — unambiguous
   * because an invoice cannot repeat an article (ArticleValidationsService
   * .checkDuplicateArticles blocks it on both the register and modify screens).
   */
  private static resolveSaleUnitId(
    value: NoteValue,
    note: Note,
  ): string | null {
    const code = value.articulo?.[0]?.codigo;
    const sales = note.invoice?.venta;
    if (!code || !sales?.length) return null;

    return sales.find((sale) => sale.codigo === code)?.id_sale_unit ?? null;
  }
}
